import {
  useState,
  useEffect,
  Fragment,
  useCallback,
  useMemo,
  useContext,
} from "react";
import { Alert } from "reactstrap";
import { DesktopContainer } from "../layouts";
import { FilterSelect } from "../components";
import { api, constants, UserContext } from "../utils";
import _ from "lodash";

export default function OrgKinotekMovementCategoryMapping() {
  const defaultOrgKinotekMovement = useMemo(
    () => ({
      id: null,
      kinotekMovementId: null,
      movementCategoryId: null,
    }),
    [],
  );

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [orgs, setOrgs] = useState([]);
  const [kinotekMovements, setKinotekMovements] = useState([]);
  const [movementCategoryOptions, setMovementCategoryOptions] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [editedMappings, setEditedMappings] = useState([]);
  const userCtx = useContext(UserContext);
  const isSysAdmin = _.includes(
    userCtx.currentUser.roleTypeIdList,
    constants.ROLE_IDS.SYSADMIN,
  );

  const transformToSelectOptions = (
    items,
    valueKey = "id",
    labelKey = "name",
  ) => items.map((item) => ({ value: item[valueKey], label: item[labelKey] }));

  const showMessage = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setMessage(null);
      setTimeout(() => setError(null), 5000);
    } else {
      setMessage(msg);
      setError(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const fetchOrgs = useCallback(() => {
    api
      .post("OrgAdmin/GetOrgSimpleList", {})
      .then((response) => {
        const orgList = response.data?.message || [];
        setOrgs(transformToSelectOptions(orgList));
      })
      .catch((error) => {
        console.error("Error fetching orgs:", error);
        showMessage("Failed to load organizations", true);
      });
  }, []);

  const fetchKinotekMovements = useCallback(() => {
    api
      .post("Kinotek/GetKinotekMovementSimpleList", {
        activeOnly: true,
        sortField: "name",
        sortDirection: "asc",
      })
      .then((response) => {
        setKinotekMovements(response.data || []);
      })
      .catch((error) => {
        console.error("Error fetching Kinotek movements:", error);
        showMessage("Failed to load Kinotek movements", true);
      });
  }, []);

  const fetchMovementCategories = useCallback((orgId) => {
    if (!orgId) return;

    api
      .post("MovementAdmin/GetMovementCategorySimpleList", {
        orgId,
        activeOnly: true,
        sortField: "name",
        sortDirection: "asc",
      })
      .then((response) => {
        const categories = response.data?.message || [];
        setMovementCategoryOptions(transformToSelectOptions(categories));
      })
      .catch((error) => {
        console.error("Error fetching movement categories:", error);
        showMessage("Failed to load movement categories", true);
      });
  }, []);

  const fetchMappings = useCallback((orgId) => {
    if (!orgId) return;

    api
      .fetch(`Kinotek/GetOrgKinotekMovementCategoryMappings/${orgId}`)
      .then((response) => {
        setMappings(response.data || []);
      })
      .catch((error) => {
        console.error("Error fetching mappings:", error);
        showMessage("Failed to load mappings", true);
      });
  }, []);

  const createCompleteMappings = useCallback(() => {
    if (!movementCategoryOptions.length || !selectedOrg) {
      setEditedMappings([]);
      return;
    }

    const completeMappings = movementCategoryOptions.map((categoryOption) => {
      const existingMapping = mappings.find(
        (m) => m.movementCategoryId === categoryOption.value,
      );

      return (
        existingMapping || {
          ...defaultOrgKinotekMovement,
          movementCategoryId: categoryOption.value,
          id: `temp-${categoryOption.value}`,
        }
      );
    });

    // finalized mapping is combination of existing and new mappings which then can be edited
    setEditedMappings(completeMappings);
  }, [
    movementCategoryOptions,
    mappings,
    selectedOrg,
    defaultOrgKinotekMovement,
  ]);

  useEffect(() => {
    fetchOrgs();
    fetchKinotekMovements();
  }, [fetchOrgs, fetchKinotekMovements]);

  // Separate effect to set user's org after orgs are loaded
  useEffect(() => {
    // If user is not sysAdmin, automatically set their org
    if (
      !isSysAdmin &&
      userCtx.currentUser.orgId &&
      orgs.length > 0 &&
      !selectedOrg
    ) {
      const userOrg = orgs.find(
        (org) => org.value === userCtx.currentUser.orgId,
      );
      if (userOrg) {
        setSelectedOrg(userOrg);
      } else {
        // Fallback if org not found in list
        const fallbackOrg = {
          value: userCtx.currentUser.orgId,
          label: userCtx.currentUser.orgName || "Unknown Organization",
        };
        setSelectedOrg(fallbackOrg);
      }
    }
  }, [
    orgs,
    isSysAdmin,
    userCtx.currentUser.orgId,
    userCtx.currentUser.orgName,
    selectedOrg,
  ]);

  useEffect(() => {
    if (selectedOrg?.value) {
      fetchMovementCategories(selectedOrg.value);
      fetchMappings(selectedOrg.value);
    } else {
      setMovementCategoryOptions([]);
      setMappings([]);
      setEditedMappings([]);
    }
  }, [selectedOrg, fetchMovementCategories, fetchMappings]);

  useEffect(() => {
    createCompleteMappings();
  }, [createCompleteMappings]);

  const handleKinotekMovementChange = (mappingId, callbackData) => {
    const selectedValue = callbackData?.value?.value || null;

    setEditedMappings((prev) =>
      prev.map((mapping) =>
        mapping.id === mappingId
          ? { ...mapping, kinotekMovementId: selectedValue }
          : mapping,
      ),
    );

    const mappingToSave = editedMappings.find((m) => m.id === mappingId);
    if (mappingToSave) {
      const isTempMapping = mappingToSave.id?.toString().startsWith("temp-");

      // Clearing a row that was never saved has nothing to persist or remove.
      if (selectedValue === null && isTempMapping) {
        return;
      }

      const updatedMapping = {
        ...mappingToSave,
        kinotekMovementId: selectedValue,
        id: isTempMapping ? null : mappingToSave.id,
      };

      saveSingleMapping(updatedMapping);
    }
  };

  const handleOrgChange = (callbackData) => {
    const selectedOption = callbackData?.value || null;
    setSelectedOrg(selectedOption);

    setMovementCategoryOptions([]);
    setMappings([]);
    setEditedMappings([]);
  };

  const saveSingleMapping = (singleMapping) => {
    api
      .post("Kinotek/SaveOrgKinotekMovementCategoryMapping", {
        mapping: singleMapping,
      })
      .then((response) => {
        if (response.status === 200) {
          showMessage("Mapping saved successfully!");
          return fetchMappings(selectedOrg.value);
        } else {
          showMessage("Failed to save mapping: " + response, true);
        }
      })
      .catch((error) => {
        console.error("Error saving mapping:", error);
        showMessage("Failed to save mapping: " + error.message, true);
      });
  };

  const getSelectedKinotekMovement = (mapping) =>
    kinotekMovements.find(
      (movement) => movement.id === mapping.kinotekMovementId,
    )
      ? transformToSelectOptions([
          kinotekMovements.find(
            (movement) => movement.id === mapping.kinotekMovementId,
          ),
        ])[0]
      : null;

  const getAvailableKinotekMovementOptions = (currentMapping) => {
    // Allow all Kinotek movements to be available for all mappings
    // A Kinotek movement can now be related to multiple Movement Categories
    return transformToSelectOptions(kinotekMovements);
  };

  return (
    <DesktopContainer>
      <Fragment>
        <div className="kinotek-movement-catergory-page-container">
          <div className="action-buttons">
            <div className="org-selection-kinotek-integration">
              {isSysAdmin ? (
                <FilterSelect
                  filterName="orgId"
                  displayName="Organization"
                  value={selectedOrg}
                  onChangeCallback={handleOrgChange}
                  options={orgs}
                  menuPlacement="auto"
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                    menuPortal: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }}
                  menuPortalTarget={document.body}
                />
              ) : (
                <div>
                  <div className="filter-definition-caption">Organization</div>
                  <div
                    style={{
                      padding: "8px 12px",
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      color: "#6c757d",
                    }}
                  >
                    {selectedOrg?.label || "Loading..."}
                  </div>
                </div>
              )}
            </div>
            <div>
              {/* Display success/error messages */}
              {message && (
                <Alert color="success" className="mb-3">
                  {message}
                </Alert>
              )}
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
            </div>
          </div>

          {selectedOrg === null && (
            <div className="no-mappings">
              Please select an organization to view movement category mappings.
            </div>
          )}

          {selectedOrg !== null && (
            <div className="mapping-table-container">
              <table className="kinotek-mapping-table">
                <thead>
                  <tr>
                    <th className="movement-category-header">
                      SPP Movement Category
                    </th>
                    <th className="kinotek-movement-header">
                      Kinotek Movement
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {editedMappings.map((mapping) => {
                    const movementCategory = movementCategoryOptions.find(
                      (category) =>
                        category.value === mapping.movementCategoryId,
                    );

                    if (!movementCategory) return null; // Skip if category not found

                    return (
                      <tr key={mapping.movementCategoryId}>
                        <td className="movement-category-cell">
                          {movementCategory.label}
                        </td>
                        <td className="kinotek-movement-cell">
                          <FilterSelect
                            filterName="kinotekMovementId"
                            displayName=""
                            value={getSelectedKinotekMovement(mapping)}
                            onChangeCallback={(callbackData) =>
                              handleKinotekMovementChange(
                                mapping.id,
                                callbackData,
                              )
                            }
                            options={getAvailableKinotekMovementOptions(
                              mapping,
                            )}
                            menuPlacement="auto"
                            styles={{
                              menu: (provided) => ({
                                ...provided,
                                zIndex: 9999,
                              }),
                              menuPortal: (provided) => ({
                                ...provided,
                                zIndex: 9999,
                              }),
                            }}
                            menuPortalTarget={document.body}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Fragment>
    </DesktopContainer>
  );
}
